import { TranslateService } from '@ngx-translate/core';
import { NotificationSeverity } from '../../models/notifications/notification-catalog-entry.model';

/**
 * A time phrase as a translation key plus its interpolation values, rather than as finished
 * English. This dashboard ships 14 languages, so the wording cannot live here — but the arithmetic
 * (which bucket, how many minutes, which month) is client-side by design (spec §12), and that part
 * does live here. `resolveTimeLabel` puts the two halves together.
 */
export interface TimeLabel {
  /** Translation key for the whole phrase. */
  key: string;
  /** Month key to resolve and pass in as `month` before translating `key`; absent otherwise. */
  monthKey?: string;
  /** Interpolation values already formatted as text or numbers. */
  params: { [name: string]: string | number };
}

const MONTH_KEYS: readonly string[] = [
  'NOTIFICATIONS.MONTH_JAN', 'NOTIFICATIONS.MONTH_FEB', 'NOTIFICATIONS.MONTH_MAR',
  'NOTIFICATIONS.MONTH_APR', 'NOTIFICATIONS.MONTH_MAY', 'NOTIFICATIONS.MONTH_JUN',
  'NOTIFICATIONS.MONTH_JUL', 'NOTIFICATIONS.MONTH_AUG', 'NOTIFICATIONS.MONTH_SEP',
  'NOTIFICATIONS.MONTH_OCT', 'NOTIFICATIONS.MONTH_NOV', 'NOTIFICATIONS.MONTH_DEC'
];

const two: (n: number) => string = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** Human relative time for feed rows; never seconds, never decimals (spec §12). */
export function relativeTime(date: Date, now: Date = new Date()): TimeLabel {
  const diffMin: number = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffMin < 1) {
    return { key: 'NOTIFICATIONS.TIME_JUST_NOW', params: {} };
  }
  if (diffMin < 60) {
    return { key: 'NOTIFICATIONS.TIME_MINUTES_AGO', params: { count: diffMin } };
  }
  const diffH: number = Math.floor(diffMin / 60);
  const sameDay: boolean = date.toDateString() === now.toDateString();
  if (sameDay && diffH < 24) {
    return { key: 'NOTIFICATIONS.TIME_HOURS_AGO', params: { count: diffH } };
  }
  const yesterday: Date = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const hm: string = `${two(date.getHours())}:${two(date.getMinutes())}`;
  if (date.toDateString() === yesterday.toDateString()) {
    return { key: 'NOTIFICATIONS.TIME_YESTERDAY_AT', params: { time: hm } };
  }
  return {
    key: 'NOTIFICATIONS.TIME_ON_DAY',
    monthKey: MONTH_KEYS[date.getMonth()],
    params: { day: date.getDate(), time: hm }
  };
}

/**
 * True when `relativeTime` produced a label that names no calendar date - which is when a feed row
 * earns the absolute stamp underneath it (spec §11.3: "whenever the relative one carries no date of
 * its own").
 *
 * Decided from the KEY, not from the rendered phrase. The design mockup tests the English text with
 * /ago$|^just now$/, which silently stops matching in the other 10 locales this dashboard ships -
 * "hace 4 minutos" ends in neither.
 */
const DATE_FREE_KEYS: readonly string[] = [
  'NOTIFICATIONS.TIME_JUST_NOW', 'NOTIFICATIONS.TIME_MINUTES_AGO', 'NOTIFICATIONS.TIME_HOURS_AGO'
];

export function showsAbsoluteTime(date: Date, now: Date = new Date()): boolean {
  return DATE_FREE_KEYS.includes(relativeTime(date, now).key);
}

/** Distinct shapes per severity so the three read apart without colour (spec §11.2). */
export function severityIcon(severity: NotificationSeverity | string): string {
  switch (severity) {
    case 'error':
      // A SQUARE, not the octagon spec 11.2 names: fa-octagon-xmark is FontAwesome Pro and this
      // app loads 6.6.0 Free (index.html:26), verified against the CDN bundle. What the spec
      // actually requires is that the three read apart WITHOUT colour, and fa-circle-xmark broke
      // exactly that - it is the same circle outline as info's fa-circle-info, so error and info
      // were distinguishable only by tint. Circle / triangle / square are three distinct shapes
      // that all exist in Free.
      return 'fa-square-xmark';
    case 'warning':
      return 'fa-triangle-exclamation';
    default:
      return 'fa-circle-info';
  }
}

/** Day bucket label for the feed page. */
export function dayLabel(date: Date, now: Date = new Date()): TimeLabel {
  if (date.toDateString() === now.toDateString()) {
    return { key: 'NOTIFICATIONS.TODAY', params: {} };
  }
  const yesterday: Date = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return { key: 'NOTIFICATIONS.YESTERDAY', params: {} };
  }
  return {
    key: 'NOTIFICATIONS.DAY_ON_DATE',
    monthKey: MONTH_KEYS[date.getMonth()],
    params: { day: date.getDate(), year: date.getFullYear() }
  };
}

/**
 * Turns a `TimeLabel` into the sentence the user reads. The month is resolved FIRST and passed in
 * as `month`, because ngx-translate does not resolve a key that appears inside an interpolated
 * value. Exported so every consumer does it the same way rather than each inventing the two-step.
 */
export function resolveTimeLabel(label: TimeLabel, translate: TranslateService): string {
  const params: { [name: string]: string | number } = label.monthKey
    ? { ...label.params, month: translate.instant(label.monthKey) }
    : label.params;
  return translate.instant(label.key, params);
}
